import { ReactElement, useRef, useState } from "react";
import { ReactComponent as LinkIcon } from "../../assets/icons/link-solid-full (1).svg";
import { useNavigate } from "react-router-dom";
import { scanUrlApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSignInModal } from "../../context/SignInModalContext";

export default function UrlScanSection(): ReactElement {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openSignInModal } = useSignInModal();

  const validateUrl = (value: string | URL) => {
    try {
      // eslint-disable-next-line no-new
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (!isAuthenticated) {
      openSignInModal();
      return;
    }
    // setResult(null); // No longer needed
    console.log("Submitting URL for scan:", url);
    if (!url.trim()) {
      setError("Please enter a URL.");
      return;
    }
    const normalizedUrl = /^https?:\/\//i.test(url.trim())
      ? url.trim()
      : `http://${url.trim()}`;
    if (!validateUrl(normalizedUrl)) {
      setError("Invalid URL format.");
      return;
    }
    setLoading(true);
    try {
      const data = await scanUrlApi(normalizedUrl, 60000);
      if (data && Array.isArray(data.resources)) {
        navigate("/url-analysis", { state: { analysisJson: data } });
      } else {
        setError(
          "No analysis data was returned. Please try again later or with a different URL.",
        );
      }
    } catch (err: any) {
      if (err.name === "AbortError") {
        setError(
          "The scan took too long and timed out. Please try again later or with a different URL.",
        );
      } else {
        setError(err.message || "Failed to scan URL.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="search-container">
      <div className="search-inner">
        <LinkIcon className="search-img" />
        <h2 className="upload-drag-drop-title">Scan a URL (beta version)</h2>
        <p className="search-text">Paste a URL to scan for threats.</p>
        <form className="searchbar" onSubmit={handleSubmit}>
          <img src="/search-grey.svg" alt="search" className="search-img-bar" />
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Paste a URL to scan for threats"
            value={url}
            onChange={(e) => {
              if (!isAuthenticated) {
                openSignInModal();
                return;
              }
              setUrl(e.target.value);
            }}
            onFocus={() => {
              if (!isAuthenticated) {
                openSignInModal();
                inputRef.current?.blur();
              }
            }}
            onClick={() => {
              if (!isAuthenticated) {
                openSignInModal();
              }
            }}
            readOnly={!isAuthenticated}
            disabled={loading}
            autoFocus
            aria-label="Paste a URL to scan for threats"
          />
          {/* <button type="submit" className="url-scan-btn" disabled={loading}>
            {loading ? "Scanning..." : "Scan URL"}
          </button> */}
        </form>
        {error && (
          <div
            className="search-error"
            style={{ color: "#b71c1c", marginTop: 5 }}
          >
            {error}
          </div>
        )}
        {/* Analysis result is now shown on /url-analysis page */}
      </div>

      {loading && (
        <div className="upload-loading-overlay">
          <div className="upload-loading-content">
            <h2>Downloading Source</h2>
            <div className="donut">
              <svg version="1.1" width="120px" height="120px">
                <g transform="translate(60, 60)">
                  <circle r="50" className="circle-back" />
                  <circle r="50" className="circle-front" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
